<template>
  <div class="mb-2 flex w-full items-center rounded-md border p-3 text-sm hover:bg-gray-50 md:text-base">
    <img
      :src="productImageToShow"
      class="mr-3 size-16 shrink-0 rounded-md object-cover"
    />
    <div class="mr-3 flex grow flex-col">
      <span class="font-medium">{{ currentProduct.name }}</span>
      <span class="text-xs font-light text-gray-500 md:text-sm">SKU: {{ currentProduct.sku }}</span>
      <span class="text-xs font-light text-gray-500 md:text-sm">
        Estado:
        <span
          :class="{
            'text-green-600': currentProduct.status === 'active',
            'text-red-600': currentProduct.status === 'inactive' || currentProduct.status === 'out_of_stock',
            'text-gray-600': currentProduct.status === 'deleted',
          }"
        >
          {{ formatStatus(currentProduct.status) }}
        </span>
      </span>
    </div>
    <div class="mr-4 flex flex-col text-right">
      <span class="text-sm font-medium">{{ useFormatCurrency(currentProduct.unitaryPriceCents) }}</span>
      <span class="text-xs text-gray-500">Precio público</span>
      <span class="text-sm text-gray-700">{{ useFormatCurrency(currentProduct.wholesalePriceCents) }}</span>
      <span class="text-xs text-gray-500">Precio mayorista</span>
    </div>
    <div class="flex flex-col items-end text-xs">
      <div class="mb-1 flex items-center space-x-2">
        <button
          class="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
          @click="(showEditDialog = true)"
        >
          Editar
        </button>
      </div>
      <div class="flex max-w-32 flex-col flex-wrap items-end gap-1">
        <span
          v-if="currentProduct.attribute1"
          class="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800"
        >
          {{ getAttributeLabel(productGroup.attribute1Type) }}: {{ currentProduct.attribute1 }}
        </span>
        <span
          v-if="currentProduct.attribute2"
          class="rounded bg-green-100 px-2 py-1 text-xs text-green-800"
        >
          {{ getAttributeLabel(productGroup.attribute2Type) }}: {{ currentProduct.attribute2 }}
        </span>
        <span
          v-if="currentProduct.attribute3"
          class="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800"
        >
          {{ getAttributeLabel(productGroup.attribute3Type) }}: {{ currentProduct.attribute3 }}
        </span>
        <span
          v-if="!currentProduct.attribute1 && !currentProduct.attribute2 && !currentProduct.attribute3"
          class="text-xs text-gray-400"
        >
          Sin variaciones
        </span>
      </div>
    </div>
    <MakerProductEditAttributesDialog
      v-if="showEditDialog"
      v-model="showEditDialog"
      :product="currentProduct"
      :product-group="productGroup"
      @attributes-updated="handleAttributesUpdated"
    />
  </div>
</template>
<script lang="ts" setup>
import { ref } from 'vue';
import useFormatCurrency from './use/format-currency';
import useProductImage from './use/product-image';
import MakerProductEditAttributesDialog from './maker-product-edit-attributes-dialog.vue';

const props = defineProps<{
  product: Product;
  productGroup: ProductGroup;
}>();

const emit = defineEmits<{
  'product-updated': [product: Product];
}>();

const showEditDialog = ref(false);
const currentProduct = ref({ ...props.product });

const { productImageToShow } = useProductImage(currentProduct.value);

const getAttributeLabel = (type: string) => {
  const labels: Record<string, string> = {
    size: 'Talla',
    color: 'Color',
    model: 'Modelo',
    not_set: 'Sin definir',
  };
  return labels[type] || type;
};

function formatStatus(status: string): string {
  const statusLabels: Record<string, string> = {
    active: 'Activo',
    inactive: 'Inactivo',
    out_of_stock: 'Sin stock',
    deleted: 'Eliminado',
  };
  return statusLabels[status] || status;
}

function handleAttributesUpdated(updatedProduct: Product) {
  currentProduct.value = { ...updatedProduct };

  emit('product-updated', updatedProduct);
}
</script>
